import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Stethoscope, CheckCircle2, XCircle, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fixed order, matching SECTION_ORDER in backend/diagnostics.py. Not alphabetical: if the
// database is down nothing below it means anything.
const SECTIONS = [
  { key: 'sys', ro: 'Infrastructură', en: 'Infrastructure' },
  { key: 'data', ro: 'Integritatea datelor', en: 'Data integrity' },
  { key: 'reg', ro: 'Regresii', en: 'Regressions' },
  { key: 'http', ro: 'Accesibilitate', en: 'Reachability' },
];

/**
 * The browser-side probes.
 *
 * These cannot live on the backend: a fetch issued from the operator's browser is the only
 * thing that proves nginx's /api route works FROM A REAL CLIENT. A backend self-check never
 * crosses the proxy, so it is structurally blind to exactly the failure that takes the
 * dashboard down while every server-side test still passes.
 */
async function runBrowserChecks(isRo) {
  const probe = async (id, label, url) => {
    try {
      const res = await fetch(url, { cache: 'no-store' });
      return {
        id, label,
        ok: res.ok,
        detail: res.ok ? 'ok' : `HTTP ${res.status}`,
      };
    } catch (err) {
      return { id, label, ok: false, detail: err.message || (isRo ? 'inaccesibil' : 'unreachable') };
    }
  };

  return Promise.all([
    probe('http.app_reachable', isRo ? 'Dashboard accesibil' : 'Dashboard reachable', window.location.origin),
    probe('http.api_health_proxied', isRo ? '/api/health prin proxy' : '/api/health through the proxy', `${API_URL}/api/health`),
    // NOT a probe of /static/js/main.js: a CRA production build emits main.<hash>.js, so
    // that path 404s on every real deployment — and it would be tautological anyway, since
    // the code running this check IS the bundle. The API root is the useful third probe:
    // it proves nginx routes /api at all, independently of whether /api/health's own
    // dependencies are healthy.
    probe('http.api_root', isRo ? 'API accesibil prin proxy' : 'API reachable through the proxy', `${API_URL}/api/`),
  ]);
}

export default function Diagnostics() {
  const { language } = useLanguage();
  const { token } = useAuth();
  const isRo = language === 'ro';

  const [checks, setChecks] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [running, setRunning] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [showPassing, setShowPassing] = useState(false);

  const run = useCallback(async () => {
    // Guarded rather than merely debounced: every data check runs a bounded but real query,
    // and a double-click would stack two full runs on the database the kiosk is using.
    setRunning(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/diagnostics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      const browser = await runBrowserChecks(isRo);
      setChecks([...(data.checks || []), ...browser]);
      setGeneratedAt(data.generated_at);
      setLoaded(true);
    } catch {
      toast.error(isRo ? 'Diagnoza nu a putut rula' : 'Diagnostics failed to run');
    } finally {
      setRunning(false);
    }
  }, [token, isRo]);

  useEffect(() => { run(); }, [run]);

  const failing = checks.filter(c => !c.ok);
  const passing = checks.filter(c => c.ok);

  const Row = ({ check }) => (
    <div className="flex items-start gap-3 py-3 border-b border-border last:border-0" data-testid={`check-${check.id}`}>
      {check.ok
        ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
        : <XCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />}
      <div className="min-w-0">
        <p className="font-medium text-foreground">{check.label}</p>
        <p className="text-sm text-muted-foreground break-words">{check.detail}</p>
        <p className="text-xs text-muted-foreground/60 font-mono">{check.id}</p>
      </div>
    </div>
  );

  const bySection = (rows) => SECTIONS
    .map(s => ({ ...s, rows: rows.filter(r => r.id.startsWith(`${s.key}.`)) }))
    .filter(s => s.rows.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Stethoscope className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {isRo ? 'Diagnoză' : 'Diagnostics'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isRo
                ? 'Verificări pentru bug-uri deja reparate — se completează la fiecare fix nou'
                : 'Checks for bugs already fixed — one is added with every new fix'}
            </p>
          </div>
        </div>
        <button
          onClick={run}
          disabled={running}
          className="flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50 transition-colors"
          data-testid="rerun-diagnostics"
        >
          <RefreshCw className={`w-4 h-4 ${running ? 'animate-spin' : ''}`} />
          {running ? (isRo ? 'Se rulează...' : 'Running...') : (isRo ? 'Rulează din nou' : 'Run again')}
        </button>
      </div>

      {!loaded && running && (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      )}

      {loaded && (
        <>
          {/* Fail-first. A panel that makes an operator scroll past twenty green rows to
              find the red one has failed at its only job. */}
          {failing.length === 0 ? (
            <div className="rounded-lg border border-border bg-card p-6 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              <p className="font-medium text-foreground">
                {isRo ? `Toate cele ${checks.length} verificări trec.` : `All ${checks.length} checks pass.`}
              </p>
            </div>
          ) : (
            bySection(failing).map(section => (
              <div key={section.key} className="rounded-lg border border-destructive/40 bg-card p-4">
                <h2 className="font-semibold text-foreground mb-1">{isRo ? section.ro : section.en}</h2>
                {section.rows.map(c => <Row key={c.id} check={c} />)}
              </div>
            ))
          )}

          {passing.length > 0 && failing.length > 0 && (
            <div className="rounded-lg border border-border bg-card">
              <button
                onClick={() => setShowPassing(v => !v)}
                className="w-full flex items-center gap-2 p-4 text-left"
                data-testid="toggle-passing"
              >
                {showPassing ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <span className="font-medium text-foreground">
                  {isRo ? `${passing.length} verificări OK` : `${passing.length} checks OK`}
                </span>
              </button>
              {showPassing && (
                <div className="px-4 pb-4">
                  {bySection(passing).map(section => (
                    <div key={section.key} className="mb-4 last:mb-0">
                      <h3 className="text-sm font-semibold text-muted-foreground mb-1">
                        {isRo ? section.ro : section.en}
                      </h3>
                      {section.rows.map(c => <Row key={c.id} check={c} />)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {generatedAt && (
            <p className="text-xs text-muted-foreground">
              {isRo ? 'Ultima rulare: ' : 'Last run: '}
              {new Date(generatedAt).toLocaleString(isRo ? 'ro-RO' : 'en-GB')}
            </p>
          )}
        </>
      )}
    </div>
  );
}
